<template>
    <div class="mechanic-file-upload-panel" tabindex="0" @paste="onPasteToPanel">
        <div class="mechanic-file-upload-row">
            <input
                    v-if="showCameraButton"
                    ref="cameraInput"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    :multiple="multiple"
                    :disabled="disabled"
                    class="mechanic-file-hidden-input"
                    @change="onCameraInputChange"
            />
            <b-form-file
                    :value="inputValue"
                    :multiple="multiple"
                    :accept="accept"
                    :disabled="disabled"
                    placeholder="Выберите файлы или перетащите их сюда"
                    drop-placeholder="Перетащите файлы сюда..."
                    browse-text="Обзор"
                    class="flex-grow-1"
                    @input="onFilesSelected"
            ></b-form-file>
            <b-button
                    v-if="showCameraButton"
                    type="button"
                    variant="outline-primary"
                    :disabled="disabled"
                    v-b-tooltip.hover
                    title="Открыть камеру телефона"
                    class="mechanic-file-camera-button"
                    @click="openCamera"
            >
                <i class="fas fa-camera mr-1"></i>
                Сфотографировать
            </b-button>
            <b-button
                    type="button"
                    variant="outline-secondary"
                    :disabled="disabled"
                    v-b-tooltip.hover
                    title="Вставить файл из буфера обмена"
                    class="mechanic-file-paste-button"
                    @click="pasteFromClipboard"
            >
                <i class="fas fa-paste mr-1"></i>
                <span class="d-none d-md-inline">Вставить из буфера</span>
            </b-button>
        </div>

        <b-alert
                v-if="cameraError"
                show
                variant="warning"
                class="mt-2 mb-0"
        >
            {{ cameraError }}
        </b-alert>

        <div v-if="showInlineCamera" class="mechanic-inline-camera mt-2">
            <div class="mechanic-inline-camera-preview">
                <video
                        ref="inlineCameraVideo"
                        autoplay
                        playsinline
                        muted
                ></video>
                <canvas
                        ref="inlineCameraFreezeCanvas"
                        v-show="cameraFreezeVisible"
                        class="mechanic-inline-camera-freeze"
                ></canvas>
                <div v-if="cameraFlashVisible" class="mechanic-inline-camera-flash"></div>
                <div v-if="cameraStarting" class="mechanic-inline-camera-loading">
                    <b-spinner small class="mr-2"></b-spinner>
                    Открываю камеру
                </div>
                <div v-if="cameraCapturing" class="mechanic-inline-camera-saving">
                    <b-spinner small class="mr-2"></b-spinner>
                    Фото сделано, добавляю файл
                </div>
            </div>
            <div class="mechanic-inline-camera-actions">
                <b-button
                        type="button"
                        variant="primary"
                        :disabled="disabled || cameraStarting || cameraCapturing || !cameraStream"
                        @click="captureInlinePhoto"
                >
                    <b-spinner v-if="cameraCapturing" small class="mr-1"></b-spinner>
                    <i v-else class="fas fa-camera mr-1"></i>
                    {{ cameraCapturing ? 'Добавляю фото' : 'Сделать фото' }}
                </b-button>
                <b-button
                        type="button"
                        variant="outline-secondary"
                        :disabled="disabled"
                        @click="openNativeCamera"
                >
                    <i class="fas fa-images mr-1"></i>
                    Системный выбор
                </b-button>
                <b-button
                        type="button"
                        variant="outline-danger"
                        :disabled="disabled"
                        @click="closeInlineCamera"
                >
                    Закрыть
                </b-button>
            </div>
        </div>

        <b-form-checkbox
                v-if="showPrimaryOption"
                :checked="makePrimary"
                :disabled="disabled"
                switch
                class="mt-2 mb-0"
                @change="$emit('update:makePrimary', Boolean($event))"
        >
            Сделать первое фото заглавным
        </b-form-checkbox>

        <div v-if="filesWithPreview.length" class="mechanic-file-preview-list mt-2">
            <div
                    v-for="(file, index) in filesWithPreview"
                    :key="file.key"
                    class="mechanic-file-preview-card"
            >
                <i
                        class="fas fa-times text-danger small mechanic-file-remove"
                        title="Удалить"
                        @click="removeFile(index)"
                ></i>
                <div
                        class="mechanic-file-preview"
                        @click="openPreview(file)"
                >
                    <img v-if="file.isImage" :src="file.previewUrl" :alt="file.name" />
                    <i v-else :class="`${file.iconClass} fa-2x text-secondary`"></i>
                </div>
                <div class="mechanic-file-name" :title="file.name">
                    {{ file.name }}
                </div>
                <small class="text-muted">{{ formatFileSize(file.size) }}</small>
            </div>
        </div>
    </div>
</template>

<script>
import clipboardFileMixin from '@/mixins/clipboardFileMixin.js';
import {
    fileIconClass,
    formatFileSize as formatViewerFileSize,
    isImageFile as isViewerImageFile
} from '@/utils/fileViewer';

export default {
    name: `MechanicAssetFileUploadPanel`,
    mixins: [clipboardFileMixin],
    props: {
        value: {
            type: Array,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        },
        multiple: {
            type: Boolean,
            default: true
        },
        accept: {
            type: String,
            default: null
        },
        showPrimaryOption: {
            type: Boolean,
            default: false
        },
        makePrimary: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            inputValue: null,
            previewUrls: new Map(),
            showInlineCamera: false,
            cameraStarting: false,
            cameraCapturing: false,
            cameraFreezeVisible: false,
            cameraFlashVisible: false,
            cameraFlashTimer: null,
            cameraFeedbackTimer: null,
            cameraStream: null,
            cameraError: null,
            cameraShotSequence: 0
        };
    },
    computed: {
        files() {
            return Array.isArray(this.value) ? this.value : [];
        },
        filesWithPreview() {
            return this.files.map((file, index) => {
                const isImage = this.isImageFile(file);
                return {
                    file,
                    index,
                    key: this.fileKey(file, index),
                    name: file.name || `Файл ${index + 1}`,
                    size: file.size || 0,
                    isImage,
                    previewUrl: this.previewUrl(file),
                    iconClass: fileIconClass(file)
                };
            });
        },
        viewerFiles() {
            return this.filesWithPreview.map(file => ({
                id: file.key,
                name: file.name,
                url: file.previewUrl,
                downloadUrl: file.previewUrl,
                thumbnailUrl: file.isImage ? file.previewUrl : null,
                mimeType: file.file && file.file.type,
                size: file.size,
                isImage: file.isImage,
                raw: file.file
            }));
        },
        showCameraButton() {
            return this.isMobileCameraDevice();
        }
    },
    watch: {
        files() {
            this.cleanupStalePreviewUrls();
        },
        disabled(value) {
            if (value) {
                this.closeInlineCamera();
            }
        }
    },
    beforeDestroy() {
        this.clearCameraFeedbackTimers();
        this.stopInlineCamera();
        this.previewUrls.forEach(url => URL.revokeObjectURL(url));
        this.previewUrls.clear();
    },
    methods: {
        toFileArray(files) {
            if (Array.isArray(files)) {
                return files;
            }
            if (files && typeof files.length === `number`) {
                return Array.from(files);
            }
            return files ? [files] : [];
        },
        onFilesSelected(files) {
            this.addFiles(files);
            this.resetNativeInputs();
        },
        onCameraInputChange(event) {
            const files = event && event.target ? event.target.files : [];
            this.addFiles(files);
            this.resetNativeInputs();
        },
        async openCamera() {
            if (this.disabled || !this.showCameraButton) {
                return;
            }
            this.cameraError = null;
            if (this.shouldUseInlineCamera()) {
                await this.openInlineCamera();
                return;
            }
            if (this.isTelegramWebApp()) {
                this.cameraError = `Встроенная камера недоступна. Открываю системный выбор файла.`;
            }
            this.openNativeCamera();
        },
        openNativeCamera() {
            if (this.disabled || !this.showCameraButton || !this.$refs.cameraInput) {
                return;
            }
            this.$refs.cameraInput.value = ``;
            this.$refs.cameraInput.click();
        },
        canUseInlineCamera() {
            return typeof navigator !== `undefined` &&
                navigator.mediaDevices &&
                typeof navigator.mediaDevices.getUserMedia === `function`;
        },
        shouldUseInlineCamera() {
            return this.isTelegramWebApp() && this.canUseInlineCamera();
        },
        isTelegramWebApp() {
            if (typeof window !== `undefined` && window.Telegram && window.Telegram.WebApp) {
                return true;
            }
            const userAgent = typeof navigator !== `undefined` ? String(navigator.userAgent || ``) : ``;
            return /Telegram/i.test(userAgent);
        },
        isMobileCameraDevice() {
            if (typeof navigator === `undefined`) {
                return false;
            }

            const userAgent = String(navigator.userAgent || ``);
            if (/Android|iPhone|iPad|iPod|IEMobile|Mobile/i.test(userAgent)) {
                return true;
            }

            const platform = String(navigator.platform || ``);
            const maxTouchPoints = Number(navigator.maxTouchPoints || 0);
            if (platform === `MacIntel` && maxTouchPoints > 1) {
                return true;
            }

            if (typeof window !== `undefined` && window.matchMedia) {
                return maxTouchPoints > 0 && window.matchMedia(`(pointer: coarse)`).matches;
            }

            return maxTouchPoints > 0;
        },
        async openInlineCamera() {
            if (this.cameraStream) {
                this.showInlineCamera = true;
                await this.attachCameraStream();
                return;
            }

            this.showInlineCamera = true;
            this.cameraStarting = true;
            this.cameraError = null;
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {
                        facingMode: {ideal: `environment`},
                        width: {ideal: 1280},
                        height: {ideal: 720}
                    }
                });
                this.cameraStream = stream;
                await this.attachCameraStream();
            } catch (err) {
                this.cameraError = `${this.cameraErrorText(err)} Пробую открыть системный выбор файла. Если он не открылся, используйте кнопку "Обзор".`;
                this.showInlineCamera = false;
                this.stopInlineCamera();
                this.openNativeCamera();
            } finally {
                this.cameraStarting = false;
            }
        },
        async attachCameraStream() {
            await this.$nextTick();
            const video = this.$refs.inlineCameraVideo;
            if (!video || !this.cameraStream) {
                return;
            }
            if (video.srcObject !== this.cameraStream) {
                video.srcObject = this.cameraStream;
            }
            const playResult = video.play ? video.play() : null;
            if (playResult && typeof playResult.catch === `function`) {
                await playResult.catch(() => {});
            }
        },
        stopInlineCamera() {
            if (this.cameraStream && this.cameraStream.getTracks) {
                this.cameraStream.getTracks().forEach(track => track.stop());
            }
            this.cameraStream = null;
            const video = this.$refs.inlineCameraVideo;
            if (video) {
                video.srcObject = null;
            }
        },
        closeInlineCamera() {
            this.showInlineCamera = false;
            this.cameraStarting = false;
            this.resetCameraCaptureFeedback();
            this.stopInlineCamera();
        },
        async captureInlinePhoto() {
            if (this.disabled || this.cameraStarting || this.cameraCapturing || !this.cameraStream) {
                return;
            }
            const video = this.$refs.inlineCameraVideo;
            if (!video) {
                this.cameraError = `Камера ещё не готова.`;
                return;
            }
            const width = video.videoWidth || video.clientWidth;
            const height = video.videoHeight || video.clientHeight;
            if (!width || !height) {
                this.cameraError = `Камера ещё не передала изображение.`;
                return;
            }

            this.cameraError = null;
            this.clearCameraFeedbackTimers();
            this.cameraFreezeVisible = false;
            this.cameraFlashVisible = false;
            this.cameraCapturing = true;
            this.drawCameraFreezeFrame(video, width, height);

            const canvas = document.createElement(`canvas`);
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext(`2d`);
            if (!context) {
                this.cameraError = `Не удалось подготовить снимок.`;
                this.resetCameraCaptureFeedback();
                return;
            }
            context.drawImage(video, 0, 0, width, height);

            let keepFeedbackVisible = false;
            try {
                const blob = await this.canvasToBlob(canvas, `image/jpeg`, 0.9);
                const file = this.createCapturedFile(blob);
                this.addFiles([file]);
                await this.$nextTick();
                this.cameraError = null;
                keepFeedbackVisible = true;
                this.cameraFeedbackTimer = setTimeout(() => {
                    this.resetCameraCaptureFeedback();
                }, 600);
            } catch (err) {
                this.cameraError = `Не удалось сделать фото: ${err && err.message ? err.message : err}`;
                this.resetCameraCaptureFeedback();
            } finally {
                if (!keepFeedbackVisible) {
                    this.cameraCapturing = false;
                }
            }
        },
        drawCameraFreezeFrame(video, width, height) {
            const canvas = this.$refs.inlineCameraFreezeCanvas;
            if (!canvas) {
                return;
            }
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext(`2d`);
            if (!context) {
                return;
            }
            context.drawImage(video, 0, 0, width, height);
            this.cameraFreezeVisible = true;
            this.cameraFlashVisible = true;
            if (this.cameraFlashTimer) {
                clearTimeout(this.cameraFlashTimer);
            }
            this.cameraFlashTimer = setTimeout(() => {
                this.cameraFlashVisible = false;
                this.cameraFlashTimer = null;
            }, 180);
        },
        resetCameraCaptureFeedback() {
            this.cameraCapturing = false;
            this.cameraFreezeVisible = false;
            this.cameraFlashVisible = false;
            this.clearCameraFeedbackTimers();
        },
        clearCameraFeedbackTimers() {
            if (this.cameraFlashTimer) {
                clearTimeout(this.cameraFlashTimer);
                this.cameraFlashTimer = null;
            }
            if (this.cameraFeedbackTimer) {
                clearTimeout(this.cameraFeedbackTimer);
                this.cameraFeedbackTimer = null;
            }
        },
        canvasToBlob(canvas, type, quality) {
            return new Promise((resolve, reject) => {
                if (canvas.toBlob) {
                    canvas.toBlob(blob => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error(`Пустой снимок`));
                        }
                    }, type, quality);
                    return;
                }
                try {
                    const dataUrl = canvas.toDataURL(type, quality);
                    const parts = dataUrl.split(`,`);
                    const byteString = atob(parts[1]);
                    const array = new Uint8Array(byteString.length);
                    for (let index = 0; index < byteString.length; index += 1) {
                        array[index] = byteString.charCodeAt(index);
                    }
                    resolve(new Blob([array], {type}));
                } catch (err) {
                    reject(err);
                }
            });
        },
        createCapturedFile(blob) {
            this.cameraShotSequence += 1;
            const name = `mechanic-photo-${this.timestampForFileName()}-${this.cameraShotSequence}.jpg`;
            if (typeof File === `function`) {
                return new File([blob], name, {
                    type: blob.type || `image/jpeg`,
                    lastModified: Date.now()
                });
            }
            blob.name = name;
            blob.lastModified = Date.now();
            return blob;
        },
        timestampForFileName() {
            const date = new Date();
            const pad = (value, length = 2) => String(value).padStart(length, `0`);
            return [
                date.getFullYear(),
                pad(date.getMonth() + 1),
                pad(date.getDate())
            ].join(`-`) + `_` + [
                pad(date.getHours()),
                pad(date.getMinutes()),
                pad(date.getSeconds()),
                pad(date.getMilliseconds(), 3)
            ].join(`-`);
        },
        cameraErrorText(err) {
            const name = err && err.name ? err.name : ``;
            if (name === `NotAllowedError` || name === `PermissionDeniedError`) {
                return `Доступ к камере запрещён.`;
            }
            if (name === `NotFoundError` || name === `DevicesNotFoundError`) {
                return `Камера не найдена.`;
            }
            if (name === `NotReadableError` || name === `TrackStartError`) {
                return `Камера занята или недоступна.`;
            }
            if (name === `OverconstrainedError` || name === `ConstraintNotSatisfiedError`) {
                return `Камера не поддерживает запрошенный режим.`;
            }
            return `Встроенная камера недоступна.`;
        },
        onPasteToPanel(event) {
            this.handlePaste(event, files => this.addFiles(files));
        },
        async pasteFromClipboard() {
            await this.handleClipboardButton(files => this.addFiles(files));
        },
        addFiles(files) {
            if (this.disabled) {
                return;
            }
            const incoming = this.toFileArray(files);
            if (!incoming.length) {
                return;
            }
            this.cameraError = null;
            const next = this.files.slice();
            incoming.forEach(file => {
                if (file) {
                    next.push(file);
                }
            });
            this.$emit(`input`, next);
            this.$emit(`change`, next);
        },
        resetNativeInputs() {
            this.$nextTick(() => {
                this.inputValue = null;
                if (this.$refs.cameraInput) {
                    this.$refs.cameraInput.value = ``;
                }
            });
        },
        removeFile(index) {
            const next = this.files.slice();
            const removed = next.splice(index, 1)[0];
            if (removed && this.previewUrls.has(removed)) {
                URL.revokeObjectURL(this.previewUrls.get(removed));
                this.previewUrls.delete(removed);
            }
            this.$emit(`input`, next);
            this.$emit(`change`, next);
        },
        openPreview(file) {
            if (!file || !file.previewUrl) {
                return;
            }
            if (!this.$fileViewer) {
                window.open(file.previewUrl, `_blank`);
                return;
            }
            this.$fileViewer.open(this.viewerFiles, {
                startId: file.key,
                source: `upload-preview`,
                pushHistory: false
            });
        },
        isImageFile(file) {
            return isViewerImageFile(file);
        },
        previewUrl(file) {
            if (!this.previewUrls.has(file)) {
                this.previewUrls.set(file, URL.createObjectURL(file));
            }
            return this.previewUrls.get(file);
        },
        cleanupStalePreviewUrls() {
            const active = new Set(this.files);
            this.previewUrls.forEach((url, file) => {
                if (!active.has(file)) {
                    URL.revokeObjectURL(url);
                    this.previewUrls.delete(file);
                }
            });
        },
        fileKey(file, index) {
            return `${file.name || `file`}_${file.size || 0}_${file.lastModified || 0}_${index}`;
        },
        formatFileSize(size) {
            return formatViewerFileSize(size) || `0 Б`;
        }
    }
};
</script>

<style scoped>
.mechanic-file-upload-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.mechanic-file-hidden-input {
    display: none;
}

.mechanic-file-camera-button,
.mechanic-file-paste-button {
    white-space: nowrap;
}

.mechanic-inline-camera {
    overflow: hidden;
    border: 1px solid #b6d4fe;
    border-radius: 4px;
    background: #f8fbff;
}

.mechanic-inline-camera-preview {
    position: relative;
    min-height: 180px;
    background: #111;
}

.mechanic-inline-camera-preview video {
    width: 100%;
    max-height: 360px;
    display: block;
    object-fit: cover;
    background: #111;
}

.mechanic-inline-camera-freeze {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 1;
    background: #111;
}

.mechanic-inline-camera-flash {
    position: absolute;
    inset: 0;
    z-index: 3;
    pointer-events: none;
    animation: mechanic-camera-flash 180ms ease-out;
    background: rgba(255, 255, 255, 0.85);
}

.mechanic-inline-camera-loading,
.mechanic-inline-camera-saving {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: rgba(0, 0, 0, 0.45);
    font-weight: 600;
}

.mechanic-inline-camera-loading {
    z-index: 2;
}

.mechanic-inline-camera-saving {
    z-index: 4;
    background: rgba(0, 0, 0, 0.58);
}

.mechanic-inline-camera-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px;
    background: #fff;
}

@keyframes mechanic-camera-flash {
    from {
        opacity: 1;
    }

    to {
        opacity: 0;
    }
}

.mechanic-file-preview-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.mechanic-file-preview-card {
    position: relative;
    width: 112px;
    text-align: center;
}

.mechanic-file-remove {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 2;
    cursor: pointer;
    background: #fff;
    border-radius: 50%;
    padding: 2px 4px;
}

.mechanic-file-preview {
    width: 112px;
    height: 84px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #f8f9fa;
    cursor: pointer;
}

.mechanic-file-preview:hover {
    border-color: #007bff;
    background: #eef5ff;
}

.mechanic-file-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.mechanic-file-name {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

@media (max-width: 576px) {
    .mechanic-file-upload-row {
        align-items: stretch;
        flex-direction: column;
    }

    .mechanic-file-camera-button,
    .mechanic-file-paste-button {
        width: 100%;
    }

    .mechanic-inline-camera-actions {
        align-items: stretch;
        flex-direction: column;
    }

    .mechanic-inline-camera-actions .btn {
        width: 100%;
    }
}
</style>
